import {cloneElement, createElement, useRef, memo, useEffect, useCallback, useState} from 'react';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {useDrag} from './hooks/useDrag';
import {SORTABLE_DIRECTION, defaultDataIdAttr, defaultClassNames, defaultGroupRole} from './constants';
import styles from './native-sortable.module.styl';

import type {FC, ComponentProps} from 'react';
import type {NativeSortableProps} from './typedef';


const classes = hashedClasses(styles);
const classNames = withCssModule(styles);

const config = {
    childList: true,
    subtree: true,
};

/**
 * NativeSortable
 *
 * Draggable sortable component that uses native HTML5 drag and drop API. It allows to drag and drop its children to reorder them.
 * **Features**
 * * Drag and drop to reorder items
 * * Horizontal and vertical directions
 * * Touch support
 * * Customizable drag handle area
 * * Callbacks for drag and drop events
 * * Cloneable children support
 * * Removable items support
 * * Disabled state
 * * Ghost element customization
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/data-display-nativesortable--documentation}
 */
export const NativeSortable: FC<NativeSortableProps> = (
    {
        allowedDragArea,
        children,
        cloneChildren = false,
        component: Component = 'div',
        dataIdAttr = defaultDataIdAttr,
        direction = SORTABLE_DIRECTION.HORIZONTAL,
        disabled = false,
        ghostMix = '',
        mix = '',
        className = '',
        onApply,
        onDragLeave,
        onDragOver,
        onDragStart,
        onDrop,
        onHover,
        removable = true,
        role = defaultGroupRole,
        targetElement,
        ...rest
    }) => {
    const refContainer = useRef<HTMLElement | null>(null);
    const [itemIds, setItemIds] = useState<string[]>([]);

    const dragEvents = useDrag({
        allowedDragArea,
        dataIdAttr,
        direction,
        disabled,
        ghostMix,
        itemIds,
        onApply,
        onDragLeave,
        onDragOver,
        onDragStart,
        onDrop,
        onHover,
        refContainer,
        removable,
        setItemIds,
        targetElement,
    });

    const updateItemIds = useCallback(() => {
        if (refContainer.current) {
            const draggableList = refContainer.current.querySelectorAll(`.${defaultClassNames.draggable}`);
            const ids = [...draggableList].map((element, index) => {
                const htmlElement = element as HTMLElement;

                if (
                    !(draggableList[0] as HTMLElement)?.dataset[defaultDataIdAttr]
                    && dataIdAttr === defaultDataIdAttr
                ) {
                    htmlElement.dataset[defaultDataIdAttr] = String(index);
                }

                return htmlElement.dataset[defaultDataIdAttr] || String(index);
            });

            setItemIds(ids);
        }
    }, [dataIdAttr]);

    useEffect(() => {
        const observer = new MutationObserver(updateItemIds);

        if (!disabled && refContainer.current) {
            observer.observe(refContainer.current, config);
            updateItemIds();
        }

        return () => observer.disconnect();
    }, [disabled, updateItemIds]);

    if (cloneChildren) {
        return cloneElement(children, {
            role,
            ref: refContainer,
            className: classNames(
                typeof children.props === 'object' && children.props && 'className' in children.props ? children.props.className : '',
                classes[defaultClassNames.nativeSortable],
                mix,
                className, {
                    'native-sortable_drag-disabled': disabled,
                },
            ),
            ...dragEvents,
            ...rest,
        } as ComponentProps<'div'>);
    }

    return createElement(
        Component,
        {
            role,
            ref: refContainer,
            className: classNames(
                defaultClassNames.nativeSortable,
                mix,
                className, {
                    'native-sortable_drag-disabled': disabled,
                },
            ),
            ...dragEvents,
            ...rest,
        },
        children,
    );
};

const Sortable: FC<NativeSortableProps> = memo<NativeSortableProps>(NativeSortable);

Sortable.displayName = 'NativeSortable';

export default Sortable;
